import { NavLink } from "react-router-dom";
import { createPendientesPage, defaultSortRows, ESTATUS2_SELECT_FIELD_OPTIONS } from "./PendientesLocalPage";
import { API_BASE_URL } from "./config";

const normalizeAseguradora = (value) =>
  (value ?? "")
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const normalizeLocalidad = (value) =>
  (value ?? "")
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const filterMultiasistenciaRows = (rows, localidadObjetivo) => {
  if (!Array.isArray(rows)) return [];
  const localidadNormalizada = normalizeLocalidad(localidadObjetivo);
  const filtered = rows.filter((row) => {
    const aseguradora = normalizeAseguradora(row?.ASEGURADORA);
    const localidad = normalizeLocalidad(row?.LOCALIDAD);
    return aseguradora.includes("multiasistencia") && localidad === localidadNormalizada;
  });
  return defaultSortRows(filtered);
};

const hasFechaCancelacion = (row) => {
  const value = row?.FECHA_CANCELACION_DE_PIEZA ?? row?.['FECHA CANCELACION DE PIEZA'] ?? '';
  return String(value ?? '').trim() !== '';
};

const normalizeStatusValue = (value) =>
  (value ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const normalizePaymentStatusValue = (value) =>
  (value ?? '')
    .toString()
    .trim()
    .toUpperCase();

const FACTURACION_EDIT_ROLES = ['administrador', 'administradores', 'supervisor', 'facturacion', 'seguimientos'];
const ESTATUS_PAGO_EDIT_ROLES = ['administrador', 'administradores', 'supervisor', 'facturacion'];
const ESTATUS_DE_PAGO_OPTIONS = ['', 'PAGADO', 'FACTURADO', 'FACTURAR', 'CANCELADO', 'REFACTURAR', 'ERROR EN COSTO', 'NO HAY ORDEN EN SIE'];

const isFacturadoPaymentStatus = (value) => normalizePaymentStatusValue(value) === 'FACTURADO';
const isPagadoPaymentStatus = (value) => normalizePaymentStatusValue(value) === 'PAGADO';
const FACTURACION_STATUS_VALUES = new Set();
const FACTURACION_PAYMENT_STATUS_VALUES = new Set(['FACTURAR', 'FACTURADO', 'PAGADO']);

const isFacturacionRow = (row) => {
  const estatus2 = normalizeStatusValue(row?.ESTATUS2);
  const estatusPago = normalizePaymentStatusValue(row?.ESTATUS_DE_PAGO);
  return FACTURACION_STATUS_VALUES.has(estatus2) || FACTURACION_PAYMENT_STATUS_VALUES.has(estatusPago);
};

const isMultiSentToFacturar = (row) => normalizePaymentStatusValue(row?.ESTATUS_DE_PAGO) === 'FACTURAR';

const isMultiCambioOperativo = (row) => normalizeStatusValue(row?.ESTATUS2) === 'cambio';
const isMultiCorreoOperativo = (row) => normalizeStatusValue(row?.ESTATUS2) === 'correo';

const isMultiReturnToOperacion = (row) => (
  isMultiCambioOperativo(row)
  || isMultiCorreoOperativo(row)
);

const isMultiErrorPortalRow = (row) => {
  const nuevoEstatus = normalizeStatusValue(row?.NUEVO_ESTATUS);
  const estatusOperativo = normalizeStatusValue(row?.ESTATUS2);
  return nuevoEstatus.includes('error') || estatusOperativo === 'terminado';
};

const shouldHideFromMultiOperacion = (row) => {
  if (isMultiReturnToOperacion(row)) return false;

  return (
    isFacturacionRow(row)
    || isMultiSentToFacturar(row)
    || hasFechaCancelacion(row)
    || isMultiErrorPortalRow(row)
  );
};

const normalizeGroupText = (value) => (value ?? '').toString().trim().toUpperCase();

const sortRowsByPedido = (rows) => {
  if (!Array.isArray(rows)) return [];
  const rowsSortedByDate = defaultSortRows(rows);
  return [...rowsSortedByDate].sort((a, b) => (
    normalizeGroupText(a?.PEDIDO).localeCompare(
      normalizeGroupText(b?.PEDIDO),
      'es',
      { numeric: true, sensitivity: 'base' }
    )
  ));
};

const getPedidoGroupStartRowClass = (params) => {
  const rowIndex = Number(params?.node?.rowIndex);
  if (!Number.isInteger(rowIndex) || rowIndex <= 0) return '';
  const previousNode = params?.api?.getDisplayedRowAtIndex?.(rowIndex - 1);
  const currentPedido = normalizeGroupText(params?.data?.PEDIDO);
  const previousPedido = normalizeGroupText(previousNode?.data?.PEDIDO);
  if (currentPedido === previousPedido) return '';
  return 'row-pedido-separador';
};

const hasBackOrderDate = (value) => String(value ?? '').trim() !== '';

const isCambioPorRecuperarStatus = (row) => {
  const normalizedEstatus2 = normalizeStatusValue(row?.ESTATUS2);
  return normalizedEstatus2.includes('cambio') || normalizedEstatus2 === 'recuperar efectivo';
};

const MULTI_NUEVO_ESTATUS_OPTIONS = Object.freeze([
  '',
  'LIBERADO',
  'CANCELADO'
]);

const MULTI_NUEVO_ESTATUS_EDIT_ROLES = [
  'administrador',
  'administradores',
  'supervisor',
  'seguimientos',
  'facturacion',
  'cancelaciones',
  'cancelacion'
];

const buildMultiAdditionalEditableFields = (fields = []) => (
  Array.from(new Set([...(Array.isArray(fields) ? fields : []), 'NUEVO_ESTATUS', 'OBSERVACION_CHOFER', 'BACK_ORDER', 'FECHA_CANCELACION_DE_PIEZA']))
);

const buildMultiSelectFieldOptions = (options = {}) => ({
  ...ESTATUS2_SELECT_FIELD_OPTIONS,
  ...(options || {}),
  NUEVO_ESTATUS: MULTI_NUEVO_ESTATUS_OPTIONS
});

const buildMultiFieldEditRoles = (roles = {}) => ({
  ...(roles || {}),
  NUEVO_ESTATUS: roles?.NUEVO_ESTATUS || MULTI_NUEVO_ESTATUS_EDIT_ROLES,
  OBSERVACION_CHOFER: roles?.OBSERVACION_CHOFER || ['chofer', 'supervisor'],
  FECHA_CANCELACION_DE_PIEZA: roles?.FECHA_CANCELACION_DE_PIEZA || ['supervisor', 'captura'],
  BACK_ORDER: roles?.BACK_ORDER || ['supervisor']
});

const MULTIASISTENCIA_SECTIONS = [
  {
    id: 'todo',
    label: 'Todo',
    subtitle: 'Todos los registros de MULTIASISTENCIA LOCAL.',
    filterFn: (row) => !shouldHideFromMultiOperacion(row)
  },
  {
    id: 'pendientes',
    label: 'Pendientes',
    subtitle: 'Registros de MULTIASISTENCIA LOCAL pendientes de seguimiento.',
    filterFn: (row) => !shouldHideFromMultiOperacion(row)
  },
  {
    id: 'cancelaciones',
    label: 'Cancelaciones',
    subtitle: 'Registros de MULTIASISTENCIA LOCAL con fecha de cancelación.',
    filterFn: (row) => (
      isMultiCambioOperativo(row)
      || (hasFechaCancelacion(row) && !isMultiCorreoOperativo(row))
    )
  },
  {
    id: 'cambios-por-recuperar',
    label: 'Cambios por recuperar',
    subtitle: 'Registros de MULTIASISTENCIA LOCAL en flujo de cambio o recuperación.',
    filterFn: (row) => isCambioPorRecuperarStatus(row) && hasBackOrderDate(row?.BACK_ORDER)
  },
  {
    id: 'recuperar-efectivo',
    label: 'Recuperar efectivo',
    subtitle: 'Registros de MULTIASISTENCIA LOCAL con estatus2 en recuperar efectivo.',
    filterFn: (row) => normalizeStatusValue(row?.ESTATUS2) === 'recuperar efectivo'
  },
  {
    id: 'facturacion',
    label: 'Facturacion',
    subtitle: 'Vista de facturación para registros de MULTIASISTENCIA LOCAL.',
    filterFn: (row) => !isMultiReturnToOperacion(row) && isFacturacionRow(row),
    sortRows: sortRowsByPedido,
    getAdditionalRowClass: getPedidoGroupStartRowClass,
    additionalEditableFields: ['NUMERO DE FACTURA', 'UUID', 'ESTATUS_DE_PAGO'],
    fieldEditRoles: {
      'NUMERO DE FACTURA': FACTURACION_EDIT_ROLES,
      UUID: FACTURACION_EDIT_ROLES,
      ESTATUS_DE_PAGO: ESTATUS_PAGO_EDIT_ROLES
    },
    selectFieldOptions: {
      ...ESTATUS2_SELECT_FIELD_OPTIONS,
      ESTATUS_DE_PAGO: ESTATUS_DE_PAGO_OPTIONS
    },
    statusQuickTabs: [
      {
        id: 'pendiente-facturar',
        label: 'Pendiente facturar',
        filterFn: (row) => {
          const estatus2 = normalizeStatusValue(row?.ESTATUS2);
          if (estatus2 !== 'facturar') return false;
          return !isFacturadoPaymentStatus(row?.ESTATUS_DE_PAGO) && !isPagadoPaymentStatus(row?.ESTATUS_DE_PAGO);
        }
      },
      {
        id: 'facturado',
        label: 'Facturado',
        filterFn: (row) => isFacturadoPaymentStatus(row?.ESTATUS_DE_PAGO)
      },
      {
        id: 'pagado',
        label: 'Pagado',
        filterFn: (row) => isPagadoPaymentStatus(row?.ESTATUS_DE_PAGO)
      }
    ],
    statusQuickTabField: 'ESTATUS_DE_PAGO',
    initialStatusQuickTab: 'pendiente-facturar'
  }
];

const SECTION_BY_ID = Object.fromEntries(
  MULTIASISTENCIA_SECTIONS.map((section) => [section.id, section])
);

const buildSectionFilter = (section, localidadObjetivo) => (rows) => {
  const baseRows = filterMultiasistenciaRows(rows, localidadObjetivo);
  const sectionRows = typeof section?.filterFn === 'function'
    ? baseRows.filter((row) => section.filterFn(row))
    : baseRows;
  if (typeof section?.sortRows === 'function') {
    return section.sortRows(sectionRows);
  }
  return sectionRows;
};


const getSelectedIds = (selectedRows) => (
  Array.isArray(selectedRows)
    ? selectedRows
      .map((row) => Number(row?.id))
      .filter((id) => Number.isInteger(id) && id > 0)
    : []
);

const postMultiasistenciaAction = async (endpoint, ids) => {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids })
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok || data?.ok === false) {
    throw new Error(data?.mensaje || 'No se pudo completar la acción.');
  }

  return data;
};

const MULTIASISTENCIA_TOOLBAR_ACTIONS = [
  {
    id: 'multi-enviar-facturar',
    label: 'ENVIAR SELECCIONADOS A FACTURAR',
    background: '#bbf7d0',
    border: '1px solid #16a34a',
    color: '#14532d',
    onClick: async ({ selectedRows }) => {
      const ids = getSelectedIds(selectedRows);
      if (!ids.length) {
        alert('Selecciona al menos un renglón para enviar a Facturar.');
        return;
      }

      if (!window.confirm(`¿Enviar ${ids.length} registro(s) seleccionado(s) a Facturar?`)) {
        return;
      }

      const data = await postMultiasistenciaAction('/api/basedatos/multiasistencia/enviar-facturar', ids);
      alert(data?.mensaje || 'Registros enviados a Facturar.');
      window.location.reload();
    }
  },
  {
    id: 'multi-enviar-cancelados',
    label: 'ENVIAR SELECCIONADOS A CANCELADOS',
    background: '#bfdbfe',
    border: '1px solid #2563eb',
    color: '#1e3a8a',
    onClick: async ({ selectedRows }) => {
      const ids = getSelectedIds(selectedRows);
      if (!ids.length) {
        alert('Selecciona al menos un renglón para enviar a Cancelados.');
        return;
      }

      if (!window.confirm(`¿Enviar ${ids.length} registro(s) seleccionado(s) a Cancelados?`)) {
        return;
      }

      const data = await postMultiasistenciaAction('/api/basedatos/multiasistencia/enviar-cancelados', ids);
      alert(data?.mensaje || 'Registros enviados a Cancelados.');
      window.location.reload();
    }
  }
];

const createMultiasistenciaSectionPage = (section, config) => createPendientesPage({
  pageTitle: `${config.titlePrefix} - ${section.label}`,
  pageSubtitle: section.subtitle.replaceAll('MULTIASISTENCIA LOCAL', config.titlePrefix),
  filterBaseRows: buildSectionFilter(section, config.localidadObjetivo),
  applyStatusGate: false,
  exportFilePrefix: `${config.exportPrefix}-${section.id}`,
  exportSheetName: `${config.titlePrefix} ${section.label}`,
  columnVisibilityStorageKey: `baseDatosColumnVisibility-${config.storageKey}`,
  hiddenColumnFields: ['ESTATUS3'],
  selectFieldOptions: buildMultiSelectFieldOptions(section?.selectFieldOptions),
  additionalEditableFields: buildMultiAdditionalEditableFields(section?.additionalEditableFields),
  fieldEditRoles: buildMultiFieldEditRoles(section?.fieldEditRoles),
  getAdditionalRowClass: section?.getAdditionalRowClass,
  statusQuickTabs: section?.statusQuickTabs,
  statusQuickTabField: section?.statusQuickTabField,
  initialStatusQuickTab: section?.initialStatusQuickTab,
  customToolbarActions: section?.id === 'facturacion' ? [] : MULTIASISTENCIA_TOOLBAR_ACTIONS
});

const MULTIASISTENCIA_LOCAL_CONFIG = {
  titlePrefix: 'MULTIASISTENCIA LOCAL',
  localidadObjetivo: 'local',
  routeBase: '/multiasistencia',
  exportPrefix: 'multiasistencia-local',
  storageKey: 'multiasistencia-local'
};

const MULTIASISTENCIA_FORANEO_CONFIG = {
  titlePrefix: 'MULTIASISTENCIA FORÁNEO',
  localidadObjetivo: 'foraneo',
  routeBase: '/multiasistencia-foraneo',
  exportPrefix: 'multiasistencia-foraneo',
  storageKey: 'multiasistencia-foraneo'
};

const MultiasistenciaPendientesInner = createMultiasistenciaSectionPage(SECTION_BY_ID['pendientes'], MULTIASISTENCIA_LOCAL_CONFIG);
const MultiasistenciaTodoInner = createMultiasistenciaSectionPage(SECTION_BY_ID['todo'], MULTIASISTENCIA_LOCAL_CONFIG);
const MultiasistenciaCancelacionesInner = createMultiasistenciaSectionPage(SECTION_BY_ID['cancelaciones'], MULTIASISTENCIA_LOCAL_CONFIG);
const MultiasistenciaCambiosInner = createMultiasistenciaSectionPage(SECTION_BY_ID['cambios-por-recuperar'], MULTIASISTENCIA_LOCAL_CONFIG);
const MultiasistenciaRecuperarEfectivoInner = createMultiasistenciaSectionPage(SECTION_BY_ID['recuperar-efectivo'], MULTIASISTENCIA_LOCAL_CONFIG);
const MultiasistenciaFacturacionInner = createMultiasistenciaSectionPage(SECTION_BY_ID['facturacion'], MULTIASISTENCIA_LOCAL_CONFIG);

const MultiasistenciaForaneoPendientesInner = createMultiasistenciaSectionPage(SECTION_BY_ID['pendientes'], MULTIASISTENCIA_FORANEO_CONFIG);
const MultiasistenciaForaneoTodoInner = createMultiasistenciaSectionPage(SECTION_BY_ID['todo'], MULTIASISTENCIA_FORANEO_CONFIG);
const MultiasistenciaForaneoCancelacionesInner = createMultiasistenciaSectionPage(SECTION_BY_ID['cancelaciones'], MULTIASISTENCIA_FORANEO_CONFIG);
const MultiasistenciaForaneoCambiosInner = createMultiasistenciaSectionPage(SECTION_BY_ID['cambios-por-recuperar'], MULTIASISTENCIA_FORANEO_CONFIG);
const MultiasistenciaForaneoRecuperarEfectivoInner = createMultiasistenciaSectionPage(SECTION_BY_ID['recuperar-efectivo'], MULTIASISTENCIA_FORANEO_CONFIG);
const MultiasistenciaForaneoFacturacionInner = createMultiasistenciaSectionPage(SECTION_BY_ID['facturacion'], MULTIASISTENCIA_FORANEO_CONFIG);

export const MultiasistenciaTodoPage = () => (
  <MultiasistenciaSectionLayout config={MULTIASISTENCIA_LOCAL_CONFIG}>
    <MultiasistenciaTodoInner />
  </MultiasistenciaSectionLayout>
);

export const MultiasistenciaForaneoTodoPage = () => (
  <MultiasistenciaSectionLayout config={MULTIASISTENCIA_FORANEO_CONFIG}>
    <MultiasistenciaForaneoTodoInner />
  </MultiasistenciaSectionLayout>
);

const getSectionLinkStyle = ({ isActive }) => ({
  padding: '8px 14px',
  borderRadius: 999,
  border: `1px solid ${isActive ? '#2563eb' : '#cbd5e1'}`,
  background: isActive ? '#dbeafe' : '#f8fafc',
  color: isActive ? '#1d4ed8' : '#0f172a',
  fontWeight: 600,
  textDecoration: 'none',
  fontSize: 13
});

const MultiasistenciaSectionLayout = ({ children, config = MULTIASISTENCIA_LOCAL_CONFIG }) => (
  <div style={{ display: 'grid', gap: 12 }}>
    <section
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: 14,
        background: '#f8fafc',
        padding: 12,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        alignItems: 'center'
      }}
    >
      <strong style={{ color: '#0f172a', marginRight: 8 }}>Secciones {config.titlePrefix}:</strong>
      {MULTIASISTENCIA_SECTIONS.map((section) => (
        <NavLink
          key={section.id}
          to={`${config.routeBase}/${section.id}`}
          style={getSectionLinkStyle}
        >
          {section.label}
        </NavLink>
      ))}
    </section>
    {children}
  </div>
);

export const MultiasistenciaPendientesPage = () => (
  <MultiasistenciaSectionLayout config={MULTIASISTENCIA_LOCAL_CONFIG}>
    <MultiasistenciaPendientesInner />
  </MultiasistenciaSectionLayout>
);

export const MultiasistenciaForaneoPendientesPage = () => (
  <MultiasistenciaSectionLayout config={MULTIASISTENCIA_FORANEO_CONFIG}>
    <MultiasistenciaForaneoPendientesInner />
  </MultiasistenciaSectionLayout>
);

export const MultiasistenciaCancelacionesPage = () => (
  <MultiasistenciaSectionLayout config={MULTIASISTENCIA_LOCAL_CONFIG}>
    <MultiasistenciaCancelacionesInner />
  </MultiasistenciaSectionLayout>
);

export const MultiasistenciaForaneoCancelacionesPage = () => (
  <MultiasistenciaSectionLayout config={MULTIASISTENCIA_FORANEO_CONFIG}>
    <MultiasistenciaForaneoCancelacionesInner />
  </MultiasistenciaSectionLayout>
);

export const MultiasistenciaCambiosPage = () => (
  <MultiasistenciaSectionLayout config={MULTIASISTENCIA_LOCAL_CONFIG}>
    <MultiasistenciaCambiosInner />
  </MultiasistenciaSectionLayout>
);

export const MultiasistenciaForaneoCambiosPage = () => (
  <MultiasistenciaSectionLayout config={MULTIASISTENCIA_FORANEO_CONFIG}>
    <MultiasistenciaForaneoCambiosInner />
  </MultiasistenciaSectionLayout>
);

export const MultiasistenciaRecuperarEfectivoPage = () => (
  <MultiasistenciaSectionLayout config={MULTIASISTENCIA_LOCAL_CONFIG}>
    <MultiasistenciaRecuperarEfectivoInner />
  </MultiasistenciaSectionLayout>
);

export const MultiasistenciaForaneoRecuperarEfectivoPage = () => (
  <MultiasistenciaSectionLayout config={MULTIASISTENCIA_FORANEO_CONFIG}>
    <MultiasistenciaForaneoRecuperarEfectivoInner />
  </MultiasistenciaSectionLayout>
);

export const MultiasistenciaFacturacionPage = () => (
  <MultiasistenciaSectionLayout config={MULTIASISTENCIA_LOCAL_CONFIG}>
    <MultiasistenciaFacturacionInner />
  </MultiasistenciaSectionLayout>
);

export const MultiasistenciaForaneoFacturacionPage = () => (
  <MultiasistenciaSectionLayout config={MULTIASISTENCIA_FORANEO_CONFIG}>
    <MultiasistenciaForaneoFacturacionInner />
  </MultiasistenciaSectionLayout>
);

const MultiasistenciaPage = MultiasistenciaTodoPage;

export default MultiasistenciaPage;
